import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, X, ChevronRight } from "lucide-react";

const SS_KEY = "trust_friction_dismissed_v1";

/**
 * FrictionFixDialog — one-tap "Finish your verification" modal that auto-
 * appears on /feed when the user is a single small action away from
 * advancing their tier. Targets exactly the stalled cohort the verification
 * funnel reveals:
 *   - KYC approved but no phone on file → phone form
 *   - Practitioner tier and no temple doc → upgrade prompt (optional)
 *
 * Skipped entirely when the missing step is heavy (KYC + selfie). Dismissed
 * via sessionStorage so it never shows twice in the same browser session.
 */
export default function FrictionFixDialog() {
  const [trust, setTrust] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem(SS_KEY) === "1") return;
    api.get("/trust/me").then(({ data }) => {
      setTrust(data);
      const c = data?.criteria || {};
      // Only show if user is a SMALL action away from leveling up.
      const phoneGap = c.kycApproved && !c.phoneVerified;
      // Don't auto-pop the Temple upgrade — that's an optional path. Keep this
      // dialog focused on the most common stall (phone after KYC).
      if (phoneGap) {
        setTimeout(() => setOpen(true), 1500);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(SS_KEY, "1");
    setOpen(false);
  };

  const submitPhone = async () => {
    if (!/^\+?[0-9\s\-()]{7,}$/.test(phone.trim())) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/users/me", { phone: phone.trim() });
      toast.success("Phone added — you're now a Verified Practitioner ✦");
      sessionStorage.setItem(SS_KEY, "1");
      setOpen(false);
      // Soft reload after a brief delay so the badge appears everywhere
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!open || !trust) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm"
      data-testid="friction-fix-dialog"
    >
      <div className="bg-card border border-secondary/30 rounded-sm p-7 w-full max-w-md space-y-5 mx-4 relative shadow-xl">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"
          data-testid="friction-fix-close"
        >
          <X size={18} />
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-violet-200">
            <Sparkles size={14} strokeWidth={1.5} />
            <span className="label-mystic">One step away</span>
          </div>
          <h2 className="font-serif text-2xl mt-1">Finish your verification.</h2>
          <p className="text-sm text-foreground/70 mt-2">
            Your government-ID is approved. Add a phone number and you become a
            <b className="text-secondary"> Verified Practitioner</b> — unlocks
            the trust badge on your profile and pushes you into the Discover
            search results.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">Phone number</label>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            className="w-full bg-input border border-secondary/20 px-3 py-2.5 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid="friction-phone-input"
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-foreground/55 text-xs uppercase tracking-wider hover:text-foreground"
            data-testid="friction-fix-skip"
          >
            Later
          </button>
          <button
            onClick={submitPhone}
            disabled={busy || !phone.trim()}
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
            data-testid="friction-fix-submit"
          >
            {busy ? "Saving..." : <>Become Verified <ChevronRight size={12} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
